import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Clock, Timer, Calendar } from 'lucide-react';
import Header from '../../../components/layout/Header';
import Footer from '../../../components/layout/Footer';
import Breadcrumbs from '../../../components/ui/Breadcrumbs';
import ToolSidebar from '../../../components/layout/ToolSidebar';
import { useTheme } from '../../../contexts/ThemeContext';
import { getToolById } from '../../../data/tools';

// Импорт компонентов таймера
import TimerForm from './CountdownTimerPage/components/TimerForm';
import TimerDisplay from './CountdownTimerPage/components/TimerDisplay';
import SavedTimers from './CountdownTimerPage/components/SavedTimers';
import QuickTimerSettings from './CountdownTimerPage/components/QuickTimerSettings';
import PopularEventsSidebar from './CountdownTimerPage/components/PopularEventsSidebar';
import TipsSidebar from './CountdownTimerPage/components/TipsSidebar';
import InfoContent from './CountdownTimerPage/components/InfoContent';
import { calculateDateDifference } from '../../../utils/calculations/date';

// Определение начального состояния формы
export const initialFormData = {
  timerName: '',
  targetDate: new Date(new Date().getTime() + 24 * 60 * 60 * 1000).toISOString().split('T')[0], // Завтра
  targetTime: '12:00',
  timerType: 'countdown', // 'countdown' или 'countup'
  includeSeconds: true,
  showYears: true,
  showMonths: true,
  showDays: true,
  showHours: true,
  showMinutes: true,
  showSeconds: true,
  theme: 'standard', // 'standard', 'dark', 'light', 'festive', 'minimalist', 'business'
  sound: 'bell', // 'bell', 'alarm', 'beep', 'chime', 'none'
  notificationType: 'browser', // 'browser', 'none'
  backgroundColor: '#ffffff',
  textColor: '#000000',
  timerMode: 'date', // 'date', 'timer'
  timerHours: 0,
  timerMinutes: 30,
  timerSeconds: 0,
};

// Популярные даты России
export const russianHolidays = [
  { id: 'new-year', name: 'Новый год', date: '2026-01-01', description: 'Главный праздник года в России' },
  { id: 'womens-day', name: 'Международный женский день', date: '2026-03-08', description: 'Праздник весны и внимания к женщинам' },
  { id: 'victory-day', name: 'День Победы', date: '2026-05-09', description: 'Праздник победы в Великой Отечественной войне' },
  { id: 'russia-day', name: 'День России', date: '2026-06-12', description: 'Государственный праздник Российской Федерации' },
  { id: 'unity-day', name: 'День народного единства', date: '2025-11-04', description: 'Праздник единства многонационального народа России' },
  { id: 'christmas', name: 'Рождество Христово', date: '2026-01-07', description: 'Один из главных христианских праздников' },
  { id: 'defender-day', name: 'День защитника Отечества', date: '2026-02-23', description: 'Праздник мужчин и военнослужащих' },
  { id: 'spring-labor', name: 'Праздник Весны и Труда', date: '2026-05-01', description: 'Традиционный весенний праздник' },
  { id: 'knowledge-day', name: 'День знаний', date: '2025-09-01', description: 'Начало учебного года в учебных заведениях России' },
  { id: 'constitution-day', name: 'День Конституции', date: '2025-12-12', description: 'День принятия действующей конституции России' },
];

// Темы оформления таймера
export const timerThemes = [
  { id: 'standard', name: 'Стандартный', primaryColor: '#3b82f6', secondaryColor: '#1e40af' },
  { id: 'dark', name: 'Тёмный', primaryColor: '#1f2937', secondaryColor: '#111827' },
  { id: 'light', name: 'Светлый', primaryColor: '#f9fafb', secondaryColor: '#f3f4f6' },
  { id: 'festive', name: 'Праздничный', primaryColor: '#ec4899', secondaryColor: '#be185d' },
  { id: 'minimalist', name: 'Минималистичный', primaryColor: '#e5e7eb', secondaryColor: '#9ca3af' },
  { id: 'business', name: 'Деловой', primaryColor: '#374151', secondaryColor: '#1f2937' },
  { id: 'russian-flag', name: 'Российский флаг', primaryColor: '#ffffff', secondaryColor: '#0039a6' },
  { id: 'victory-day', name: 'День Победы', primaryColor: '#ff7043', secondaryColor: '#000000' },
];

// Звуки уведомлений
export const soundOptions = [
  { id: 'bell', name: 'Колокольчик' },
  { id: 'alarm', name: 'Будильник' },
  { id: 'beep', name: 'Сигнал' },
  { id: 'chime', name: 'Перезвон' },
  { id: 'none', name: 'Без звука' },
];

/**
 * CountdownTimerPage Component - Полный инструмент для создания и управления таймерами обратного отсчёта.
 */
const CountdownTimerPage = () => {
  const { subToolId } = useParams();
  const { darkMode, toggleDarkMode } = useTheme();

  // --- Данные инструмента ---
  const tool = useMemo(() => getToolById('countdown-timer'), []);
  const pageTitle = tool?.metaTitle || 'Таймер обратного отсчёта | Gensio';
  const metaDescription = tool?.metaDescription || 'Создайте таймер обратного отсчёта до важного события, праздника или дедлайна. Настраиваемый интерфейс, уведомления и возможность сохранения нескольких таймеров.';

  // --- Управление состоянием ---
  const [formData, setFormData] = useState(initialFormData);
  const [timerInterval, setTimerInterval] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState({});
  const [isRunning, setIsRunning] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [errors, setErrors] = useState({});
  const [savedTimers, setSavedTimers] = useState([]);
  const [activeTimerId, setActiveTimerId] = useState(null);

  // --- Обновление заголовка и мета-описания ---
  useEffect(() => {
    document.title = pageTitle;
    const metaTag = document.querySelector('meta[name="description"]') || document.createElement('meta');
    metaTag.name = 'description';
    metaTag.setAttribute('content', metaDescription);
    if (!document.querySelector('meta[name="description"]')) {
      document.head.appendChild(metaTag);
    }
  }, [pageTitle, metaDescription]);

  // --- Загрузка сохраненных таймеров из localStorage ---
  useEffect(() => {
    try {
      const savedTimersData = localStorage.getItem('savedTimers');
      if (savedTimersData) {
        setSavedTimers(JSON.parse(savedTimersData));
      }
    } catch (error) {
      console.error("Ошибка загрузки сохраненных таймеров:", error);
    }
  }, []);

  // --- Обработка subToolId для предварительной настройки формы ---
  useEffect(() => {
    if (!subToolId) return;

    setFormData(prev => {
      let newState = { ...initialFormData };

      switch (subToolId) {
        case 'countdown':
          newState.timerType = 'countdown';
          break;
        case 'countup':
          newState.timerType = 'countup';
          break;
        case 'new-year':
        case 'womens-day':
        case 'victory-day':
        case 'russia-day':
        case 'unity-day':
        case 'christmas':
        case 'defender-day':
        case 'spring-labor':
        case 'knowledge-day':
        case 'constitution-day':
          const holiday = russianHolidays.find(h => h.id === subToolId);
          if (holiday) {
            newState.timerName = `Обратный отсчёт до ${holiday.name}`;
            newState.targetDate = holiday.date;
            newState.timerType = 'countdown';
            newState.timerMode = 'date';
            if (subToolId === 'new-year' || subToolId === 'christmas') {
              newState.theme = 'festive';
            } else if (subToolId === 'victory-day') {
              newState.theme = 'victory-day';
            } else if (subToolId === 'russia-day' || subToolId === 'unity-day') {
              newState.theme = 'russian-flag';
            }
          }
          break;
        case 'timer':
          newState.timerMode = 'timer';
          break;
        default:
          newState = { ...prev };
      }
      return newState;
    });

  }, [subToolId]);

  // --- Функция обновления времени таймера ---
  const updateTimer = (targetDateObj, mode, hours = 0, minutes = 0, seconds = 0) => {
    if (isCompleted) return;

    const now = new Date();
    let timeDiff;

    if (mode === 'date') {
      // Для обратного отсчета до даты
      timeDiff = calculateDateDifference(now, targetDateObj);
      
      if (formData.timerType === 'countdown') {
        if (now >= targetDateObj) {
          clearInterval(timerInterval);
          setIsRunning(false);
          setIsCompleted(true);
          
          if (formData.sound !== 'none' && formData.notificationType !== 'none') {
            playSound(formData.sound);
            showNotification(formData.timerName || 'Таймер', 'Отсчёт завершен!');
          }
          
          return;
        }
      }
    } else {
      // Для простого таймера (часы, минуты, секунды)
      const totalSeconds = hours * 3600 + minutes * 60 + seconds;
      const elapsedTime = Math.floor((now - startTimeRef.current) / 1000);
      
      if (formData.timerType === 'countdown') {
        const remainingSeconds = totalSeconds - elapsedTime;
        if (remainingSeconds <= 0) {
          clearInterval(timerInterval);
          setIsRunning(false);
          setIsCompleted(true);
          
          if (formData.sound !== 'none' && formData.notificationType !== 'none') {
            playSound(formData.sound);
            showNotification(formData.timerName || 'Таймер', 'Отсчёт завершен!');
          }
          
          return;
        }
        
        timeDiff = {
          years: 0,
          months: 0,
          days: 0,
          hours: Math.floor(remainingSeconds / 3600),
          minutes: Math.floor((remainingSeconds % 3600) / 60),
          seconds: remainingSeconds % 60,
          milliseconds: 0
        };
      } else {
        timeDiff = {
          years: 0,
          months: 0,
          days: 0,
          hours: Math.floor(elapsedTime / 3600),
          minutes: Math.floor((elapsedTime % 3600) / 60),
          seconds: elapsedTime % 60,
          milliseconds: 0
        };
      }
    }

    setTimeRemaining(timeDiff);
  };

  // --- Референс для отслеживания времени старта таймера ---
  const startTimeRef = React.useRef(null);

  // --- Запуск таймера ---
  const startTimer = () => {
    if (isRunning && !isPaused) return;
    
    if (isPaused) {
      setIsPaused(false);
    } else {
      setIsRunning(true);
      setIsCompleted(false);
      startTimeRef.current = new Date();
    }
    
    let targetDateObj;
    if (formData.timerMode === 'date') {
      targetDateObj = new Date(`${formData.targetDate}T${formData.targetTime || '00:00'}:00`);
    }
    
    const interval = setInterval(() => {
      if (formData.timerMode === 'date') {
        updateTimer(targetDateObj, 'date');
      } else {
        updateTimer(null, 'timer', formData.timerHours, formData.timerMinutes, formData.timerSeconds);
      }
    }, 1000);
    
    setTimerInterval(interval);
    
    // Первое обновление
    if (formData.timerMode === 'date') {
      updateTimer(targetDateObj, 'date');
    } else {
      updateTimer(null, 'timer', formData.timerHours, formData.timerMinutes, formData.timerSeconds);
    }
  };

  // --- Остановка таймера ---
  const stopTimer = () => {
    clearInterval(timerInterval);
    setIsRunning(false);
    setIsPaused(false);
    setTimeRemaining({});
  };

  // --- Пауза таймера ---
  const pauseTimer = () => {
    clearInterval(timerInterval);
    setIsPaused(true);
  };

  // --- Сброс таймера ---
  const resetTimer = () => {
    clearInterval(timerInterval);
    setIsRunning(false);
    setIsPaused(false);
    setIsCompleted(false);
    setTimeRemaining({});
  };

  // --- Воспроизведение звука ---
  const playSound = (soundType) => {
    // Здесь можно реализовать проигрывание разных звуков
    try {
      const audio = new Audio(`/sounds/${soundType}.mp3`);
      audio.play();
    } catch (error) {
      console.error("Ошибка воспроизведения звука:", error);
    }
  };

  // --- Показ браузерного уведомления ---
  const showNotification = (title, body) => {
    if (!("Notification" in window)) {
      console.log("Этот браузер не поддерживает уведомления");
      return;
    }
    
    if (Notification.permission === "granted") {
      new Notification(title, { body });
    } else if (Notification.permission !== "denied") {
      Notification.requestPermission().then(permission => {
        if (permission === "granted") {
          new Notification(title, { body });
        }
      });
    }
  };

  // --- Сохранение таймера ---
  const saveTimer = () => {
    if (!formData.timerName) {
      setErrors(prev => ({ ...prev, timerName: 'Пожалуйста, укажите название таймера' }));
      return;
    }

    const newTimer = {
      id: Date.now().toString(),
      ...formData,
      createdAt: new Date().toISOString()
    };

    const updatedTimers = [...savedTimers, newTimer];
    setSavedTimers(updatedTimers);
    
    try {
      localStorage.setItem('savedTimers', JSON.stringify(updatedTimers));
    } catch (error) {
      console.error("Ошибка сохранения таймера:", error);
    }
    
    return newTimer;
  };

  // --- Загрузка сохраненного таймера ---
  const loadTimer = (id) => {
    const timerToLoad = savedTimers.find(timer => timer.id === id);
    if (timerToLoad) {
      setFormData(timerToLoad);
      setActiveTimerId(id);
      resetTimer();
    }
  };

  // --- Удаление сохраненного таймера ---
  const deleteTimer = (id) => {
    const updatedTimers = savedTimers.filter(timer => timer.id !== id);
    setSavedTimers(updatedTimers);
    
    try {
      localStorage.setItem('savedTimers', JSON.stringify(updatedTimers));
    } catch (error) {
      console.error("Ошибка удаления таймера:", error);
    }
    
    if (activeTimerId === id) {
      setActiveTimerId(null);
    }
  };

  // --- Эффект очистки при размонтировании ---
  useEffect(() => {
    return () => {
      if (timerInterval) {
        clearInterval(timerInterval);
      }
    };
  }, [timerInterval]);

  // --- RENDER ---
  return (
    <div className={`min-h-screen ${darkMode ? 'dark' : ''} bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-200 font-sans`}>
      {/* Background Pattern */}
      <div aria-hidden="true" className="fixed inset-0 bg-[radial-gradient(theme(colors.gray.200)_1px,transparent_1px)] dark:bg-[radial-gradient(theme(colors.gray.800)_1px,transparent_1px)] [background-size:24px_24px] opacity-20 dark:opacity-10 pointer-events-none z-[-1]"></div>

      <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} />

      <main className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumbs />

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          {/* --- Main Content Area --- */}
          <div className="lg:w-2/3 flex-shrink-0">
            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-3">
                <Timer className="w-8 h-8 text-indigo-600 dark:text-indigo-400 flex-shrink-0" />
                {tool?.name || 'Таймер обратного отсчёта'}
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                {metaDescription}
              </p>
            </div>

            {/* --- Таймер --- */}
            <TimerDisplay 
              timeRemaining={timeRemaining}
              formData={formData}
              isRunning={isRunning}
              isPaused={isPaused}
              isCompleted={isCompleted}
              startTimer={startTimer}
              pauseTimer={pauseTimer}
              stopTimer={stopTimer}
              resetTimer={resetTimer}
            />

            {/* --- Форма настройки таймера --- */}
            <TimerForm 
              formData={formData}
              setFormData={setFormData}
              timerThemes={timerThemes}
              soundOptions={soundOptions}
              errors={errors}
              setErrors={setErrors}
              saveTimer={saveTimer}
              startTimer={startTimer}
              isRunning={isRunning}
              russianHolidays={russianHolidays}
            />

            {/* --- Таблица сохраненных таймеров --- */}
            <SavedTimers 
              savedTimers={savedTimers}
              loadTimer={loadTimer}
              deleteTimer={deleteTimer}
              activeTimerId={activeTimerId}
            />

            {/* --- Информационный контент о таймерах --- */}
            <InfoContent />
          </div>

          {/* --- Sidebar Area --- */}
          <aside className="lg:w-1/3 self-start space-y-8">
            {/* --- Панель быстрой настройки --- */}
            <QuickTimerSettings 
              formData={formData}
              setFormData={setFormData}
              timerThemes={timerThemes}
              startTimer={startTimer}
              stopTimer={stopTimer}
              resetTimer={resetTimer}
              isRunning={isRunning}
            />

            {/* --- Популярные события и праздники --- */}
            <PopularEventsSidebar 
              russianHolidays={russianHolidays}
              setFormData={setFormData}
              loadPreset={loadTimer}
            />

            {/* --- Панель полезных советов --- */}
            <TipsSidebar />

            {/* --- Связанные инструменты --- */}
            {tool && (
              <ToolSidebar 
                currentToolId={tool.id} 
                currentSubToolId={subToolId} 
                tool={tool} 
              />
            )}
          </aside>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default CountdownTimerPage;